import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  inject,
} from '@angular/core';
import { documentOf, windowOf } from '../anchor-document.util';

/**
 * Directive that displays any HTMLElement as a floating tooltip that follows the cursor.
 *
 * Attach it to the host element and pass the tooltip element reference via the input.
 * The tooltip element will be positioned using `position: fixed` and will follow the
 * cursor similar to the behaviour of the TooltipCharts component.
 */
@Directive({
  selector: '[libTooltipFollow]',
  standalone: true,
})
export class TooltipFollowDirective implements OnDestroy {
  /**
   * Tooltip element that will be shown while the user hovers the host element.
   */
  @Input('libTooltipFollow')
  set tooltip(value: ElementRef<HTMLElement> | HTMLElement | null | undefined) {
    this.tooltipElement = this.resolveElement(value);
    if (this.tooltipElement) {
      this.prepareTooltipElement();
    }
  }

  /** Horizontal offset applied to the tooltip position. */
  @Input() tooltipFollowOffsetX = 12;
  /** Vertical offset applied to the tooltip position. */
  @Input() tooltipFollowOffsetY = 12;
  /** Allows disabling the tooltip behaviour without removing the directive. */
  @Input() tooltipFollowDisabled = false;
  /** Custom z-index applied to the floating tooltip. */
  @Input() tooltipFollowZIndex = 1000;
  /** Delay in milliseconds before showing the tooltip. Default is 0 (no delay). */
  @Input() tooltipFollowDebounceTime = 0;

  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly hostElement: ElementRef<HTMLElement> = inject(ElementRef);

  private tooltipElement: HTMLElement | null = null;
  private tooltipInitialDisplay: string | null = null;
  private readonly windowRef = this.document?.defaultView ?? null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingMouseEvent: MouseEvent | null = null;
  /** `body` onde o balão está montado — o do documento da âncora na altura. */
  private appendedTo: HTMLElement | null = null;
  /** Janela cujo `mousemove` o balão está a seguir. */
  private mouseMoveTarget: Window | null = null;
  private repositionFrameId: number | null = null;
  private repositionView: Window | null = null;

  /**
   * Documento da âncora. Não é sempre o global: no mini-monitor destacado
   * (Document Picture-in-Picture) a âncora está noutra janela, e é no `body`, na
   * viewport e no `mousemove` dela que o balão vive. Lido a cada uso porque a
   * âncora pode mudar de documento enquanto a diretiva vive.
   */
  private get anchorDocument(): Document {
    return documentOf(this.hostElement.nativeElement) ?? this.document;
  }

  private get anchorWindow(): Window | null {
    return this.anchorDocument.defaultView ?? this.windowRef;
  }

  private listenMouseMove(): void {
    this.mouseMoveTarget?.removeEventListener('mousemove', this.handleMouseMove);
    this.mouseMoveTarget = this.anchorWindow;
    this.mouseMoveTarget?.addEventListener('mousemove', this.handleMouseMove, { passive: true });
  }

  private readonly handleMouseMove = (event: MouseEvent): void => {
    if (!this.tooltipElement) {
      return;
    }
    this.updateTooltipPosition(event);
  };

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    if (this.tooltipFollowDisabled || !this.tooltipElement || !this.anchorWindow) {
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.pendingMouseEvent = event;

    if (this.tooltipFollowDebounceTime === 0) {
      this.showTooltip(event);
      this.listenMouseMove();
    } else {
      this.debounceTimer = setTimeout(() => {
        if (this.pendingMouseEvent) {
          this.showTooltip(this.pendingMouseEvent);
          this.listenMouseMove();
        }
        this.debounceTimer = null;
        this.pendingMouseEvent = null;
      }, this.tooltipFollowDebounceTime);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.pendingMouseEvent = null;
    }
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingMouseEvent = null;
    this.hideTooltip(true);
    if (this.appendedTo && this.tooltipElement?.parentNode === this.appendedTo) {
      this.renderer.removeChild(this.appendedTo, this.tooltipElement);
    }
    this.appendedTo = null;
  }

  /** Ensure tooltip has the required base styles and cached display value. */
  private prepareTooltipElement(): void {
    const view = windowOf(this.tooltipElement) ?? this.anchorWindow;
    if (!this.tooltipElement || !view) {
      return;
    }

    const computedStyles = view.getComputedStyle(this.tooltipElement);
    if (!this.tooltipInitialDisplay) {
      this.tooltipInitialDisplay = computedStyles.display !== 'none' ? computedStyles.display : null;
    }

    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'top', '0');
    this.renderer.setStyle(this.tooltipElement, 'left', '0');
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.tooltipElement, 'z-index', String(this.tooltipFollowZIndex));
    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
    this.renderer.setStyle(this.tooltipElement, 'transform', 'translate3d(-9999px, -9999px, 0)');
  }

  /** Display tooltip and align it with the mouse cursor. */
  private showTooltip(event: MouseEvent): void {
    if (!this.tooltipElement) {
      return;
    }

    this.prepareTooltipElement();

    const body = this.anchorDocument.body;
    if (body && this.tooltipElement.parentNode !== body) {
      this.renderer.appendChild(body, this.tooltipElement);
      this.appendedTo = body;
    }

    this.renderer.setStyle(this.tooltipElement, 'display', this.tooltipInitialDisplay ?? 'block');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '1');

    this.updateTooltipPosition(event);

    // O conteudo do balao costuma vir de um signal que o proprio `mouseenter`
    // acabou de escrever, por isso nesta primeira passagem medimos uma caixa
    // ainda por preencher e o balao assenta no sitio errado ate ao primeiro
    // `mousemove`. Reposicionar no frame seguinte ja apanha o layout final, sem
    // atrasar o aparecimento como faria um debounce.
    this.cancelPendingReposition();
    this.repositionView = this.anchorWindow;
    this.repositionFrameId = this.repositionView?.requestAnimationFrame(() => {
      this.repositionFrameId = null;
      if (this.tooltipElement) {
        this.updateTooltipPosition(event);
      }
    }) ?? null;
  }

  private cancelPendingReposition(): void {
    if (this.repositionFrameId !== null) {
      this.repositionView?.cancelAnimationFrame(this.repositionFrameId);
      this.repositionFrameId = null;
    }
  }

  /** Hide tooltip and cleanup listeners. */
  private hideTooltip(forceCleanup: boolean = false): void {
    this.cancelPendingReposition();
    this.mouseMoveTarget?.removeEventListener('mousemove', this.handleMouseMove);
    this.mouseMoveTarget = null;

    if (!this.tooltipElement) {
      return;
    }

    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');

    if (forceCleanup) {
      this.renderer.removeStyle(this.tooltipElement, 'transform');
      this.renderer.removeStyle(this.tooltipElement, 'pointer-events');
      this.renderer.removeStyle(this.tooltipElement, 'top');
      this.renderer.removeStyle(this.tooltipElement, 'left');
      this.renderer.removeStyle(this.tooltipElement, 'position');
      this.renderer.removeStyle(this.tooltipElement, 'z-index');
      if (this.tooltipInitialDisplay) {
        this.renderer.setStyle(this.tooltipElement, 'display', this.tooltipInitialDisplay);
      } else {
        this.renderer.removeStyle(this.tooltipElement, 'display');
      }
    }
  }

  /** Update tooltip coordinates based on current mouse position. Positions tooltip on the opposite side of screen edges to avoid overflow. */
  private updateTooltipPosition(event: MouseEvent): void {
    const view = this.anchorWindow;
    if (!this.tooltipElement || !view) {
      return;
    }

    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const { innerWidth, innerHeight } = view;
    const offsetX = this.tooltipFollowOffsetX;
    const offsetY = this.tooltipFollowOffsetY;

    // Prefer opening to the side opposite to screen edges (away from nearest edge)
    let left: number;
    if (event.clientX < innerWidth / 2) {
      left = event.clientX + offsetX; // Cursor in left half → tooltip to the right of cursor
    } else {
      left = event.clientX - tooltipRect.width - offsetX; // Cursor in right half → tooltip to the left of cursor
    }

    let top: number;
    if (event.clientY < innerHeight / 2) {
      top = event.clientY + offsetY; // Cursor in top half → tooltip below cursor
    } else {
      top = event.clientY - tooltipRect.height - offsetY; // Cursor in bottom half → tooltip above cursor
    }

    // Clamp to viewport so tooltip never overflows
    left = Math.max(0, Math.min(left, innerWidth - tooltipRect.width));
    top = Math.max(0, Math.min(top, innerHeight - tooltipRect.height));

    this.renderer.setStyle(this.tooltipElement, 'transform', `translate3d(${left}px, ${top}px, 0)`);
  }

  private resolveElement(value?: ElementRef<HTMLElement> | HTMLElement | null): HTMLElement | null {
    if (!value) {
      return null;
    }

    if (value instanceof ElementRef) {
      return value.nativeElement;
    }

    return value;
  }
}
